import { useEffect, useState } from 'react';
import { useLoanHistoryStore, fieldLabelMap } from '@/hooks/useSupabaseStore';

interface LoanHistoryPanelProps {
  loanId: string;
}

const statusLabelMap: Record<string, string> = {
  active: 'Đang vay',
  overdue: 'Quá hạn',
  due_soon: 'Sắp đến hạn',
  interest_paid: 'Đã đóng lãi',
  redeemed: 'Đã chuộc',
  forfeited: 'Tịch thu',
};

function formatValue(field: string, value: string): string {
  if (!value) return '(trống)';
  if (field === 'status' && statusLabelMap[value]) return statusLabelMap[value];
  if (['loan_amount', 'interest_rate'].includes(field)) {
    const num = Number(value);
    if (!isNaN(num)) {
      if (field === 'loan_amount') return `${num.toLocaleString('vi-VN')}đ`;
      return `${num}%`;
    }
  }
  return value;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function LoanHistoryPanel({ loanId }: LoanHistoryPanelProps) {
  const { history, loading, fetchHistory } = useLoanHistoryStore();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded && loanId) {
      fetchHistory(loanId);
    }
  }, [expanded, loanId, fetchHistory]);

  const toggle = () => setExpanded(v => !v);

  return (
    <div className="border-t border-gray-100 pt-4">
      <button
        onClick={toggle}
        className="flex items-center gap-2 w-full text-left cursor-pointer group"
      >
        <div className="w-7 h-7 flex items-center justify-center bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
          <i className={`ri-time-line text-gray-500 text-sm transition-transform ${expanded ? 'rotate-12' : ''}`}></i>
        </div>
        <span className="text-sm font-semibold text-gray-700">Lịch sử chỉnh sửa</span>
        {history.length > 0 && !loading && (
          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
            {history.length} lần
          </span>
        )}
        <span className="ml-auto w-4 h-4 flex items-center justify-center text-gray-400">
          <i className={`ri-arrow-down-s-line text-base transition-transform ${expanded ? 'rotate-180' : ''}`}></i>
        </span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1">
          {loading && (
            <div className="flex items-center gap-2 py-3 text-gray-400 text-xs">
              <span className="w-4 h-4 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin"></span>
              Đang tải lịch sử...
            </div>
          )}

          {!loading && history.length === 0 && (
            <p className="text-xs text-gray-400 py-2">Chưa có lần chỉnh sửa nào được ghi nhận.</p>
          )}

          {!loading && history.map((h) => (
            <div
              key={h.id}
              className="bg-gray-50 rounded-lg px-3 py-2.5 text-xs space-y-1"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-700">
                  {fieldLabelMap[h.fieldName] || h.fieldName}
                </span>
                <span className="text-gray-400 text-[11px]">{formatDateTime(h.changedAt)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-500">
                <span className="text-gray-400 line-through">{formatValue(h.fieldName, h.oldValue)}</span>
                <span className="w-3 h-3 flex items-center justify-center text-gray-300">
                  <i className="ri-arrow-right-line"></i>
                </span>
                <span className="font-medium text-gray-700">{formatValue(h.fieldName, h.newValue)}</span>
              </div>
              <p className="text-[11px] text-gray-400">
                bởi <span className="font-medium text-gray-500">{h.changedBy}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}